import type { AmpFileSort } from "src/graphql";
import {
  DEFAULT_LIBRARY_SORT,
  isSort,
  LIBRARY_FOLDER_PARAM,
  LIBRARY_TYPES,
  type LibraryType,
} from "src/pages/projects/library/library-filters";
import { useLocation, useSearchParams } from "src/router";

/** The Library URL's query params for the file list's search, filters, and sort. */
const LIST_PARAMS = {
  search: "q",
  fileTypes: "type",
  brandProfileIds: "brand",
  sort: "sort",
  mine: "mine",
} as const;

export type LibraryListFilters = {
  /** As typed, so a trailing space survives the round trip. */
  search: string;
  /** In Library order. */
  fileTypes: LibraryType[];
  /** Sorted, so the same selection always shares a cache key. */
  brandProfileIds: string[];
  sort: AmpFileSort;
  /** Only the files the viewer created, where the Library offers that switch. */
  mine: boolean;
};

const listOf = (value: string | null) =>
  value ? value.split(",").filter(Boolean) : [];

/** The file list's search, filters, and sort from a Library URL, dropping values it doesn't know. */
export function parseLibraryListFilters(
  params: URLSearchParams,
): LibraryListFilters {
  const fileTypes = new Set(listOf(params.get(LIST_PARAMS.fileTypes)));
  const sort = params.get(LIST_PARAMS.sort);
  return {
    search: params.get(LIST_PARAMS.search) ?? "",
    fileTypes: LIBRARY_TYPES.filter((type) => fileTypes.has(type)),
    brandProfileIds: [
      ...new Set(listOf(params.get(LIST_PARAMS.brandProfileIds))),
    ].sort(),
    sort: sort && isSort(sort) ? sort : DEFAULT_LIBRARY_SORT,
    mine: params.get(LIST_PARAMS.mine) === "true",
  };
}

/** A copy of the params with the given filters written in, leaving out empty values and the default sort. */
export function writeLibraryListFilters(
  params: URLSearchParams,
  filters: Partial<LibraryListFilters>,
): URLSearchParams {
  const next = new URLSearchParams(params);
  const write = (key: string, value: string) => {
    if (value) next.set(key, value);
    else next.delete(key);
  };
  if (filters.search !== undefined) write(LIST_PARAMS.search, filters.search);
  if (filters.fileTypes)
    write(
      LIST_PARAMS.fileTypes,
      LIBRARY_TYPES.filter((type) => filters.fileTypes?.includes(type)).join(
        ",",
      ),
    );
  if (filters.brandProfileIds)
    write(
      LIST_PARAMS.brandProfileIds,
      [...new Set(filters.brandProfileIds)].sort().join(","),
    );
  if (filters.sort)
    write(
      LIST_PARAMS.sort,
      filters.sort === DEFAULT_LIBRARY_SORT ? "" : filters.sort,
    );
  if (filters.mine !== undefined)
    write(LIST_PARAMS.mine, filters.mine ? "true" : "");
  return next;
}

/** Keeps list filters across folders; a type view replaces the explicit file type selection. */
export function selectLibraryFolder(
  params: URLSearchParams,
  folderId: string | null,
): URLSearchParams {
  const next = new URLSearchParams(params);
  if (folderId) next.set(LIBRARY_FOLDER_PARAM, folderId);
  else next.delete(LIBRARY_FOLDER_PARAM);
  if (LIBRARY_TYPES.some((type) => type === folderId)) {
    next.delete(LIST_PARAMS.fileTypes);
  }
  return next;
}

/** The file list's search, filters, and sort, which the URL holds so a link opens the same list. */
export function useLibraryListFilters() {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  return {
    filters: parseLibraryListFilters(searchParams),
    // Replaces the entry, so Back leaves the list rather than undoing each keystroke or filter.
    setFilters: (filters: Partial<LibraryListFilters>) =>
      setSearchParams((current) => writeLibraryListFilters(current, filters), {
        replace: true,
        state: location.state,
      }),
  };
}
